/**
 * Уведомление о данных для клиентов салона (ссылка в шапке профиля Instagram и на странице записи).
 * Салон: оператор (руковалац) данных своих клиентов; мы: обработчик по договору (ZZPL, стандартные клаузулы
 * Поверенника). ЧЕРНОВИК: тексты должен проверить юрист (`stage-6-status.md`). Русского нет, показываем английский.
 */
export interface NoticeSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface NoticeText {
  title: string;
  draft: string;
  intro: (salon: string) => string;
  sections: (salon: string) => NoticeSection[];
  privacyLink: string;
}

export const NOTICE: Record<"sr" | "en", NoticeText> = {
  en: {
    title: "How we handle your data",
    draft: "Draft: under legal review.",
    intro: (salon) =>
      `${salon} uses the AI Administrator service to answer messages and manage bookings. This notice explains what happens with your data when you write to ${salon} on Instagram or book online.`,
    sections: (salon) => [
      {
        heading: "Who is responsible",
        paragraphs: [
          `${salon} is the controller of your data. AI Administrator processes the data on behalf of ${salon} under a data processing agreement and only to provide the service to the salon.`,
        ],
      },
      {
        heading: "What we keep",
        list: [
          "Your name, phone number and Instagram username, if you give them or write from Instagram.",
          "Messages you send to the salon and the replies, including automatic replies.",
          "Your bookings: services, master, date and time, cancellations and no-shows.",
          "Voice messages are converted to text and the audio is not kept. Photos and files are not kept.",
          "Messages about health (for example, allergies) go straight to the salon staff and are not sent to the AI model.",
        ],
      },
      {
        heading: "Why",
        list: [
          "To answer your questions and book, move or cancel your appointments.",
          "So the salon staff can see your bookings and contact you about them.",
          "We do not send newsletters or advertising and do not sell your data.",
        ],
      },
      {
        heading: "Who else processes the data",
        paragraphs: [
          "Anthropic (AI model, USA), OpenAI (voice-to-text, USA), Railway (hosting; processing also in the USA), Meta (Instagram). The USA is not on Serbia's list of countries with adequate data protection.",
        ],
      },
      {
        heading: "How long",
        paragraphs: [`While ${salon} uses the service. If the salon leaves the service, the data is erased 30 days after its request.`],
      },
      {
        heading: "Your rights",
        paragraphs: [
          `You can ask ${salon} to show, correct or delete your data, or to stop processing it. Write to the salon on Instagram or ask in person. You can also complain to the Commissioner for Information of Public Importance and Personal Data Protection of Serbia (poverenik.rs).`,
        ],
      },
    ],
    privacyLink: "Privacy policy of the AI Administrator service",
  },
  sr: {
    title: "Kako postupamo s vašim podacima",
    draft: "Nacrt: čeka pravnu proveru.",
    intro: (salon) =>
      `${salon} koristi uslugu AI Administrator za odgovaranje na poruke i zakazivanje termina. Ovo obaveštenje objašnjava šta se dešava s vašim podacima kada pišete salonu ${salon} na Instagramu ili zakažete termin onlajn.`,
    sections: (salon) => [
      {
        heading: "Ko je odgovoran",
        paragraphs: [
          `${salon} je rukovalac vašim podacima. AI Administrator obrađuje podatke u ime salona ${salon} na osnovu ugovora o obradi i samo radi pružanja usluge salonu.`,
        ],
      },
      {
        heading: "Šta čuvamo",
        list: [
          "Vaše ime, broj telefona i korisničko ime na Instagramu, ako ih navedete ili pišete sa Instagrama.",
          "Poruke koje šaljete salonu i odgovore, uključujući automatske.",
          "Vaše termine: usluge, frizera, datum i vreme, otkazivanja i nedolaske.",
          "Glasovne poruke se pretvaraju u tekst, a snimak se ne čuva. Fotografije i fajlovi se ne čuvaju.",
          "Poruke o zdravlju (na primer, alergije) idu direktno osoblju salona i ne šalju se AI modelu.",
        ],
      },
      {
        heading: "Zašto",
        list: [
          "Da odgovorimo na vaša pitanja i da zakažemo, pomerimo ili otkažemo vaš termin.",
          "Da osoblje salona vidi vaše termine i može da vas kontaktira u vezi s njima.",
          "Ne šaljemo bilten ni reklame i ne prodajemo vaše podatke.",
        ],
      },
      {
        heading: "Ko još obrađuje podatke",
        paragraphs: [
          "Anthropic (AI model, SAD), OpenAI (pretvaranje glasa u tekst, SAD), Railway (hosting; obrada i u SAD), Meta (Instagram). SAD nisu na listi zemalja s primerenim nivoom zaštite podataka u Srbiji.",
        ],
      },
      {
        heading: "Koliko dugo",
        paragraphs: [`Dok ${salon} koristi uslugu. Ako salon prestane da koristi uslugu, podaci se brišu 30 dana nakon njegovog zahteva.`],
      },
      {
        heading: "Vaša prava",
        paragraphs: [
          `Možete tražiti od salona ${salon} da vam pokaže, ispravi ili obriše podatke ili da prestane da ih obrađuje. Pišite salonu na Instagramu ili pitajte lično. Možete podneti i pritužbu Povereniku za informacije od javnog značaja i zaštitu podataka o ličnosti (poverenik.rs).`,
        ],
      },
    ],
    privacyLink: "Politika privatnosti usluge AI Administrator",
  },
};
